import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, tap, of, catchError, throwError, from, switchMap } from 'rxjs';
import { TokenStorageService } from './token-storage.service';
import { AuthApiService } from './auth-api.service';
import { ChangePasswordRequest, ConfirmPinRequest, LoginData, LoginSuccessData, MiniUserDto, RefreshData, RegisterRequest, UserDto } from '../_model/users';
import { Router } from '@angular/router';
import { ACCOUNT_USER_ROLES, ADMIN_ROLES, STAFF_ROLES, UserRole, hasAnyRole } from '../_model/user-role';
import { DeviceFingerprintService } from './device-fingerprint.service';

export type LoginResult =
  | { kind: 'totp'; expiresAt: string }
  | { kind: 'ok'; data: LoginSuccessData };


@Injectable({ providedIn: 'root' })
export class AuthService {
  private loggedIn$: BehaviorSubject<boolean>;
  isLoggedIn$: Observable<boolean>;

  private currentUserSubject = new BehaviorSubject<MiniUserDto | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private api: AuthApiService,
    private tokens: TokenStorageService,
    private router: Router,
    private deviceFingerprintService: DeviceFingerprintService
  ) {
    this.loggedIn$ = new BehaviorSubject<boolean>(!!this.tokens.getAccessToken());
    this.isLoggedIn$ = this.loggedIn$.asObservable();
  }

  get accessToken(): string | null {
    return this.tokens.getAccessToken();
  }

  get tempToken(): string | null {
    return this.tokens.getTempToken();
  }

  get tokenType(): string {
    return this.tokens.getTokenType() ?? 'Bearer';
  }

  hasAnyRole$(roles: readonly UserRole[]): Observable<boolean> {
    return this.getCurrentUser().pipe(
      map(user => hasAnyRole(user?.role, roles))
    );
  }

  hasAnyRoleSync(roles: readonly UserRole[]): boolean {
    return hasAnyRole(this.currentUserSubject.value?.role, roles);
  }

  isAccountUser$(): Observable<boolean> {
    return this.hasAnyRole$(ACCOUNT_USER_ROLES);
  }

  isAdmin$(): Observable<boolean> {
    return this.hasAnyRole$(ADMIN_ROLES);
  }

  isStaff$(): Observable<boolean> {
    return this.hasAnyRole$(STAFF_ROLES);
  }

  get isAccountUserSync(): boolean {
    return this.hasAnyRoleSync(ACCOUNT_USER_ROLES);
  }

  get isAdminSync(): boolean {
    return this.hasAnyRoleSync(ADMIN_ROLES);
  }

  get isStaffSync(): boolean {
    return this.hasAnyRoleSync(STAFF_ROLES);
  }

  get currentRole(): UserRole | null {
    return (this.currentUserSubject.value?.role as UserRole | undefined) ?? null;
  }

  getCurrentUser(): Observable<MiniUserDto | null> {
    const token = this.tokens.getAccessToken();
    const exp = this.tokens.getAccessExpiresAt();
    const expired = exp ? new Date(exp).getTime() <= Date.now() : false;

    if (!token || expired) {
      this.forceLogout(this.isCurrentRouteProtected());
      return of(null);
    }

    if (this.currentUserSubject.value) return of(this.currentUserSubject.value);

    return this.api.me().pipe(
      map(res => res?.data ?? null),
      tap(u => {
        if (!u) {
          this.clearUser();
          this.loggedIn$.next(false);
          return;
        }

        this.currentUserSubject.next(u);
        this.loggedIn$.next(true);
      }),
      catchError(() => {
        this.clearUser();
        this.loggedIn$.next(false);
        return of(null);
      })
    );
  }

  setCurrentUser(user: MiniUserDto) {
    this.currentUserSubject.next(user);
  }

  clearUser() {
    this.currentUserSubject.next(null);
  }
  
  login(email: string, password: string): Observable<LoginResult> {
    return from(this.buildDevicePayload()).pipe(
      switchMap(({ deviceFingerprint, deviceName }) =>
        this.api.login({ email, password, deviceFingerprint, deviceName })
      ),
      map(res => res.data as LoginData),
      tap((d) => {
        if ('requiresTotp' in d && d.requiresTotp) {
          this.tokens.setTotpChallengeToken(d.challengeToken);
          return;
        }

        const ok = d as LoginSuccessData;

        this.tokens.setTokens({
          accessToken: ok.accessToken,
          refreshToken: ok.refreshToken,
          tokenType: ok.tokenType ?? 'Bearer',
          expiresAt: ok.expiresAt,
          refreshTokenExpiresAt: ok.refreshTokenExpiresAt,
        });

        this.setCurrentUser(ok.user);
        this.loggedIn$.next(true);
      }),
      map((d) => {
        if ('requiresTotp' in d && d.requiresTotp) {
          return { kind: 'totp', expiresAt: d.expiresAt };
        }
        return { kind: 'ok', data: d as LoginSuccessData };
      })
    );
  }

  loginWithPin(email: string, pin: string): Observable<LoginResult> {
    return from(this.buildDevicePayload()).pipe(
      switchMap(({ deviceFingerprint, deviceName }) =>
        this.api.loginWithPin({ email, pin, deviceFingerprint, deviceName })
      ),
      map(res => res.data as LoginData),
      tap((d) => {
        if ('requiresTotp' in d && d.requiresTotp) {
          this.tokens.setTotpChallengeToken(d.challengeToken);
          return;
        }

        const ok = d as LoginSuccessData;
        this.tokens.setTokens({
          accessToken: ok.accessToken,
          refreshToken: ok.refreshToken,
          tokenType: ok.tokenType ?? 'Bearer',
          expiresAt: ok.expiresAt,
          refreshTokenExpiresAt: ok.refreshTokenExpiresAt,
        });

        this.setCurrentUser(ok.user);
        this.loggedIn$.next(true);
      }),
      map((d) => {
        if ('requiresTotp' in d && d.requiresTotp) {
          return { kind: 'totp', expiresAt: d.expiresAt } as LoginResult;
        }
        return { kind: 'ok', data: d as LoginSuccessData } as LoginResult;
      })
    );
  }

  private async buildDevicePayload(): Promise<{ deviceFingerprint: string; deviceName: string }> {
    const deviceFingerprint = await this.deviceFingerprintService.getDeviceFingerprint();
    const deviceName = this.deviceFingerprintService.getDeviceName();

    return {
      deviceFingerprint,
      deviceName
    };
  }

  verifyLoginTotp(code: string): Observable<LoginSuccessData> {
    const challengeToken = this.tokens.getTotpChallengeToken();
    if (!challengeToken) throw new Error('Missing challenge token');

    return this.api.loginTotp({ challengeToken, code }).pipe(
      map(res => res.data),
      tap(ok => {
        this.tokens.setTokens({
          accessToken: ok.accessToken,
          refreshToken: ok.refreshToken,
          tokenType: ok.tokenType ?? 'Bearer',
          expiresAt: ok.expiresAt,
          refreshTokenExpiresAt: ok.refreshTokenExpiresAt,
        });

        this.tokens.clearTotpChallengeToken();
        this.setCurrentUser(ok.user);
        this.loggedIn$.next(true);
      })
    );
  }

  register(payload: RegisterRequest) {
    return this.api.register(payload).pipe(map(r => r));
  }

  logout(redirectUrl: string = '/') {
    const refreshToken = this.tokens.getRefreshToken();

    const call$ = refreshToken
      ? this.api.logout(refreshToken).pipe(map(() => void 0))
      : of(void 0);

    return call$.pipe(
      catchError(() => of(void 0)), // swallow API errors
      tap(() => {
        this.tokens.clear();
        this.clearUser();
        this.loggedIn$.next(false);

        this.router.navigateByUrl(redirectUrl, { replaceUrl: true });
      })
    );
  }

  confirmPin(pin: string) {
    const token = this.tokens.getTempToken();
    if (!token) throw new Error('No access token found');

    const payload: ConfirmPinRequest = { token, pin };
    return this.api.confirmPin(payload);
  }

  resendPin() {
    const token = this.tokens.getTempToken();
    if (!token) throw new Error('No access token found');

    return this.api.resendPin({ token });
  }

  changePassword(currentPassword: string, newPassword: string, confirmNewPassword?: string) {
    const payload: ChangePasswordRequest = { currentPassword, newPassword, confirmNewPassword };

    return this.api.changePassword(payload).pipe(
      map(r => r.data!), // API returns tokens in data
      tap((t) => {
        this.tokens.setTokens({
          accessToken: t.accessToken,
          refreshToken: t.refreshToken,
          tokenType: t.tokenType,
          expiresAt: t.expiresAt,
          refreshTokenExpiresAt: t.refreshTokenExpiresAt,
        });
      })
    );
  }

  refreshAccessToken(): Observable<RefreshData> {
    const refreshToken = this.tokens.getRefreshToken();
    if (!refreshToken) return throwError(() => new Error('No refresh token'));

    return this.api.refresh({ refreshToken }).pipe(
      map(res => res.data),
      tap((d) => {
        this.tokens.setTokens({
          accessToken: d.accessToken,
          refreshToken: d.refreshToken,
          tokenType: d.tokenType,
          expiresAt: d.expiresAt,
          refreshTokenExpiresAt: d.refreshTokenExpiresAt,
        });
        this.clearUser();
        this.getCurrentUser().subscribe();
      })
    );
  }

  forceLogout(redirectToLogin = true, returnUrl = this.router.url) {
    this.tokens.clear();
    this.clearUser();
    this.loggedIn$.next(false);

    if (redirectToLogin && !this.isAuthRoute(returnUrl)) {
      this.router.navigate(['/auth/login'], {
        queryParams: { returnUrl },
        replaceUrl: true
      });
    }
  }

  isCurrentRouteProtected(url = this.router.url): boolean {
    const path = this.normalizePath(url);

    if (this.isAuthRoute(path)) return false;
    if (path === '/' || path === '/markets' || path === '/support') return false;

    return [
      '/account',
      '/admin',
      '/compliance',
      '/dashboard',
      '/orders',
      '/review',
      '/support/tickets',
      '/wallets'
    ].some(prefix => path === prefix || path.startsWith(`${prefix}/`));
  }

  private isAuthRoute(url: string): boolean {
    const path = this.normalizePath(url);
    return path === '/auth' || path.startsWith('/auth/');
  }

  private normalizePath(url: string): string {
    const tree = this.router.parseUrl(url || '/');
    const segments = tree.root.children['primary']?.segments.map(segment => segment.path) ?? [];
    return `/${segments.join('/')}`.replace(/\/+$/, '') || '/';
  }

  verifyLoginRecovery(code: string): Observable<LoginSuccessData> {
    const challengeToken = this.tokens.getTotpChallengeToken();
    if (!challengeToken) return throwError(() => new Error('Missing challenge token'));

    return this.api.loginRecovery({ challengeToken, code }).pipe(
      map(res => res.data),
      tap((ok) => {
        this.tokens.setTokens({
          accessToken: ok.accessToken,
          refreshToken: ok.refreshToken,
          tokenType: ok.tokenType ?? 'Bearer',
          expiresAt: ok.expiresAt,
          refreshTokenExpiresAt: ok.refreshTokenExpiresAt,
        });

        this.tokens.clearTotpChallengeToken();

        this.setCurrentUser(ok.user);
        this.loggedIn$.next(true);
      })
    );
  }
  public shouldShow2faReminderSync(): boolean {
    const u = this.currentUserSubject.value;
    return !!u && shouldShow2faReminder(u);
  }

  public updateOtpReminder(nextReminder: string | null) {
    return this.api.updateOtpReminder(nextReminder);
  }
}

export function shouldShow2faReminder(user: UserDto): boolean {
  const otp = user.otp;
  if (!otp) return false;

  // show only when 2FA disabled AND nextReminder exists AND is past
  if (otp.otpEnabled) return false;
  if (!otp.otpNextReminder) return false;

  return new Date(otp.otpNextReminder).getTime() <= Date.now();
}
